// @flow
/* -------------------- Globals ----------------*/
import produce from 'immer';
import findLast from 'lodash/findLast';

/* -------------------- Helpers ----------------*/
import {
  setTarget,
  addAnswer,
  skipQuestion,
} from '../functions/progressReducer';

/* ----------------------------- Functions --------------- */
import flipScore from '../functions/scoring/flip';
/* -------------------- Utils ----------------*/
import { summarizeGame } from '../functions/summary';
import {
  countQuestions,
} from '../functions/distributionReducer';
import { timeStamp, isEmptyList } from '../simpleUtils';
import {
  THRESHOLD_MASTERY_NORMAL_SESSION,
  THRESHOLD_MASTERY_SCHEDULED_SESSION,
} from '../session/constants';
/* -------------------- Action Types ----------------*/
import {
  ANSWER,
  BACK,
  COMPLETE_SESSION,
  FLIP,
  SKIP,
  NEXT,
  START_SESSION,
  STOP_SESSION,
  UNFLIP,
  SHOW,
  START_NEXT_BATCH,
} from './actionTypes';

import {
  RESET_APP,
} from '../actionTypes';

/* -------------------- Types ----------------*/
import {
  flipEventType,
} from './types';
import type {
  FlashCardCollection,
  FlipEventType,
  FlipState,
  FlipAnswer,
  CardBack,
} from './types';

import {
  initialState,
} from './constants';

const summarize = (state: FlipState,
  data: *) => produce(state, (draft) => {
  const {
    answers,
    startTime,
    endTime,
  } = state;
  const scores = [];
  answers.forEach(({ cardBack }) => {
    cardBack.forEach(({ score }) => {
      scores.push(score);
    });
  });
  draft.summary = summarizeGame(
    scores,
    startTime,
    endTime,
  );
  if(data && data[0])
  draft.reviewedContents = data[0].sessionTags;
});

const factIds = (a, c) => {
  const acc = a;
  c.cardBack.forEach(({ factId }) => {
    acc.push(factId);
  });
  return acc;
};
const handleReset = state => produce(state, (draft) => {
  draft.uniqueAnswers = {};
  draft.answeredFacts = {};
  draft.events = [];
  draft.answers = [];
  draft.isLastCardVisible = false;
  draft.cardStates = [
    {
      answerSeen: false,
      cardFlipped: false,
      confidenceProvided: false,
    },
  ];
  draft.progressData = {
    target: [],
    answered: [],
    skipped: [],
  };
  draft.currentIndex = 0;
  draft.flashCards = {
    termCards: [],
    factCards: [],
  };
  draft.distribution = {
    none: 0,
    low: 0,
    high: 0,
    medium: 0,
  };
  draft.requestedQuestionsCount = 0;
  draft.hasMore = false;
});

const handleStartSession = (
  state: FlipState,
  data: *,
) => produce(state, (draft) => {
  const {
    inReverseMode,
    cards: flashCards,
    hasMore,
    isSchedule,
    id: sessionId,
    learningOrder,
    scheduleId,
    distribution,
    numGoal,
  } = data;
  const cards = inReverseMode ? flashCards.factCards : flashCards.termCards;
  const ids = cards.reduce(factIds, []);
  const progressData = setTarget(ids);
  draft.currentIndex = 0;
  const { contentId } = cards[0];
  draft.hasMore = hasMore;
  draft.flashCards = flashCards;
  draft.isSchedule = isSchedule;
  draft.cardStates = cards.map(() => ({
    answerSeen: false,
    cardFlipped: false,
    confidenceProvided: false,
  }));
  draft.sessionId = sessionId;
  draft.contextId = contentId;
  draft.learningOrder = learningOrder;
  draft.inReverseMode = inReverseMode;
  draft.progressData = progressData;
  draft.sessionCreated = true;
  draft.scheduleId = scheduleId;
  draft.requestedQuestionsCount = numGoal || countQuestions(distribution);
});

const handleFlipNextBatch = (
  state: FlipState,
  flashCards: FlashCardCollection,
  inReverseMode: boolean,
  hasMore: boolean,
) => produce(state, (draft) => {
  const {
    flashCards: currentFlashCards,
    flashCards: { termCards: currentTermCards, factCards: currentFactCards },
    cardStates,
    progressData: { skipped, answered },
  } = state;

  const { termCards, factCards } = flashCards;
  const currentCards = inReverseMode
    ? currentFlashCards.factCards
    : currentFlashCards.termCards;

  const newCards = inReverseMode
    ? flashCards.factCards
    : flashCards.termCards;
  const cards = [...currentCards, ...newCards];
  const ids = cards.reduce(factIds, []);
  draft.hasMore = hasMore;
  draft.progressData = setTarget(ids, answered, skipped);
  const newCardStates = newCards.map(() => ({
    answerSeen: false,
    cardFlipped: false,
    confidenceProvided: false,
  }));
  const newFlashcards = {
    factCards: [...currentFactCards, ...factCards],
    termCards: [...currentTermCards, ...termCards],
  };
  draft.flashCards = newFlashcards;
  draft.cardStates = [...cardStates, ...newCardStates];
  draft.isLastCardVisible = !isEmptyList(newCards);
});

const recordEvent = (
  currentIndex: number,
  state: FlipState,
  eventType: FlipEventType,
) => produce(state, (draft) => {
  const {
    flashCards,
    inReverseMode,
    sessionId,
    contextId,
    events,
    cardStates,
    startTime,
  } = state;
  if (flashCards && sessionId && contextId) {
    if (!startTime) {
      const newStartTime = new Date().toISOString();
      draft.startTime = newStartTime;
    }
    const cards = inReverseMode
      ? flashCards.factCards
      : flashCards.termCards;
    if (cards && cards.length) {
      draft.cardStates = cardStates;
      const {
        itemId,
        prompt,
        cardBack,
        contentId,
      } = cards[currentIndex];
      const eventContext = {
        sessionId,
        contextId: contentId,
        time: new Date().toISOString(),
      };
      const cardFront = {
        itemId,
        prompt,
      };
      const cardBackIds = inReverseMode ? cardBack.map(card => card.termId)
        : cardBack.map(card => card.factId);
      const flipItem = {
        cardBackIds,
        cardFront,
      };
      const eventPayload = {
        inReverseMode,
        flipItem,
      };
      const newEvent = {
        eventContext,
        eventType,
        eventPayload,
      };
      draft.events = [...events, newEvent];
      draft.contextId = cards[currentIndex - 1] ? cards[currentIndex - 1]
        .contentId : cards[currentIndex].contentId;
    }
  }
});

const recordAnswer = (
  currentIndex: number,
  state: FlipState,
  confidence: number,
  itemId: string,
  cardBack: CardBack[],
  timeTaken: number,
  contentId: string,
): FlipAnswer => {
  const {
    flashCards,
    inReverseMode,
    cardStates: oldStates,
  } = state;
  const cards = inReverseMode
    ? flashCards.factCards
    : flashCards.termCards;
  const cardBacks = cards[currentIndex].cardBack;
  const pairs = cardBacks.map(({ termId, factId }) => ({ termId, factId }));
  const cardStates = [...oldStates];
  cardStates[currentIndex] = {
    cardFlipped: true,
    answerSeen: true,
    confidenceProvided: true,
  };

  return {
    pairs,
    inReverseMode,
    confidence,
    cardStates,
    itemId,
    cardBack,
    timeTaken,
    contentId,
  };
};

const recordFlip = (state: FlipState) => {
  const {
    currentIndex,
    cardStates: oldStates,
  } = state;

  const cardStates = [...oldStates];

  cardStates[currentIndex] = {
    cardFlipped: true,
    answerSeen: true,
    confidenceProvided: oldStates[currentIndex].confidenceProvided,
  };

  return {
    ...state,
    cardStates,
  };
};

const recordUnflip = (state: FlipState) => {
  const {
    cardStates: oldStates,
    currentIndex,
  } = state;

  const cardStates = [...oldStates];
  cardStates[currentIndex] = {
    cardFlipped: false,
    answerSeen: true,
    confidenceProvided: oldStates[currentIndex].confidenceProvided,
  };

  return {
    ...state,
    cardStates,
  };
};

const handleShow = (state: FlipState) => recordEvent(
  state.currentIndex,
  state,
  flipEventType.SHOW,
);

const handleFlip = (state: FlipState) => recordEvent(
  state.currentIndex,
  recordFlip(state),
  flipEventType.FLIP,
);

const handleUnFlip = (state: FlipState) => recordEvent(
  state.currentIndex,
  recordUnflip(state),
  flipEventType.UNFLIP,
);

const handleSkip = (state: FlipState) => {
  const {
    lastSeenIndex,
    currentIndex,
    inReverseMode,
    progressData,
    flashCards,
  } = state;
  const cards = inReverseMode
    ? flashCards.factCards
    : flashCards.termCards;
  const {
    cardBack,
  } = cards[currentIndex];
  let newProgressData = progressData;
  cardBack.forEach(({
    factId,
  }) => {
    newProgressData = skipQuestion(progressData, factId);
  });

  let newLastSeenIndex = lastSeenIndex;
  if (lastSeenIndex < currentIndex + 1) {
    newLastSeenIndex = currentIndex + 1;
  }
  return recordEvent(
    currentIndex,
    {
      ...state,
      currentIndex: cards[currentIndex + 1] ? currentIndex + 1 : currentIndex,
      progressData: newProgressData,
      lastSeenIndex: newLastSeenIndex,
      isLastCardVisible: !cards[currentIndex + 1],
    },
    flipEventType.SKIP,
  );
};

const handleBack = (state: FlipState) => {
  const { currentIndex } = state;
  return recordEvent(
    currentIndex,
    {
      ...state,
      currentIndex: currentIndex - 1,
    },
    flipEventType.BACK,
  );
};

const handleNext = (state: FlipState) => {
  const { currentIndex, inReverseMode, flashCards } = state;
  const cards = inReverseMode
    ? flashCards.factCards
    : flashCards.termCards;
  return recordEvent(
    currentIndex,
    {
      ...state,
      currentIndex: currentIndex + 1,
      isLastCardVisible: !cards[currentIndex + 1],
    },
    flipEventType.NEXT,
  );
};

const handleAnswer = (state: FlipState, confidence, shouldUpdateIndex?: boolean = true) => {
  const {
    currentIndex,
    inReverseMode,
    progressData,
    flashCards,
    answers,
    lastSeenIndex,
    events,
    cardStates: oldStates,
    uniqueAnswers,
    isSchedule,
    answeredFacts,
    sessionMeta,
  } = state;
  const cards = inReverseMode
    ? flashCards.factCards
    : flashCards.termCards;
  const currentCard = cards[currentIndex];
  const endTime = timeStamp();
  const showEvent = findLast(events,
    event => event.eventType === flipEventType.SHOW
    && event.eventPayload.flipItem.cardFront.itemId === currentCard.itemId);
  const time = showEvent && showEvent.eventContext ? showEvent.eventContext.time : 0;
  const startTime = new Date(time).getTime();
  const timeTaken = Math.min((endTime - startTime) / 1000, 300);

  const newSessionMeta = { ...sessionMeta };
  if (currentCard.itemId in sessionMeta) {
    newSessionMeta[currentCard.itemId] = {
      lastTime: endTime,
      numSuccessAttempts: confidence === 100
        ? sessionMeta[currentCard.itemId].numSuccessAttempts + 1 : 0,
      numTotalAttempts: (sessionMeta[currentCard.itemId].numTotalAttempts + 1),
    };
  } else {
    newSessionMeta[currentCard.itemId] = {
      lastTime: endTime,
      numSuccessAttempts: confidence === 100 ? 1 : 0,
      numTotalAttempts: 1,
    };
  }

  const {
    cardBack,
    contentId,
    itemId,
  } = currentCard;
  const thresold = isSchedule ? THRESHOLD_MASTERY_SCHEDULED_SESSION
    : THRESHOLD_MASTERY_NORMAL_SESSION;

  let newProgressData = progressData;
  const newCardBack = [];
  cardBack.forEach(({
    currentScore,
    factId,
    termId,
    previousScore,
  }, index) => {
    const { score, timeFactor } = flipScore(
      currentCard,
      previousScore,
      confidence,
      timeTaken,
      sessionMeta,
    );

    newCardBack.push({ ...cardBack[index], score, timeFactor });

    newProgressData = addAnswer(progressData, factId);
    const previousScoreValue = currentScore ? currentScore.value : undefined;

    const currentAnsweredFact = {
      previousScore: previousScoreValue,
      currentScore: score,
      termId,
      studySetId: contentId,
    };
    answeredFacts[factId] = currentAnsweredFact;
    const previouslyHigh = previousScoreValue ? previousScoreValue >= thresold : false;
    const isHigh = score >= thresold;
    const isItemAnswered = uniqueAnswers[factId];
    if (!isItemAnswered) {
      const newuniqueAnswerItem = {
        score,
        high: isHigh,
        previouslyHigh,
        contentId: currentCard.contentId,
      };
      uniqueAnswers[factId] = newuniqueAnswerItem;
    } else if (uniqueAnswers && isItemAnswered) {
      uniqueAnswers[factId] = {
        score,
        high: isHigh,
        previouslyHigh,
        contentId: currentCard.contentId,
      };
    }
  });
  let newLastSeenIndex = lastSeenIndex;
  if (lastSeenIndex < currentIndex + 1) {
    newLastSeenIndex = currentIndex + 1;
  }
  const newAnswer = recordAnswer(
    currentIndex,
    state,
    confidence,
    itemId,
    newCardBack,
    timeTaken,
    contentId,
  );

  oldStates[currentIndex].confidenceProvided = true;

  return recordEvent(
    currentIndex,
    {
      ...state,
      ...(shouldUpdateIndex ? {
        currentIndex:
        cards[currentIndex + 1] ? currentIndex + 1 : currentIndex,
      } : {}),
      progressData: newProgressData,
      lastSeenIndex: newLastSeenIndex,
      answers: [...answers, newAnswer],
      uniqueAnswers,
      cardStates: oldStates,
      sessionMeta: { ...newSessionMeta },
      isLastCardVisible: !cards[currentIndex + 1],
    },
    flipEventType.ANSWER,
  );
};

const handleEndSession = (
  state: FlipState,
  data: *,
) => {
  const { currentIndex } = state;
  const newState = recordEvent(
    currentIndex,
    state,
    flipEventType.TERMINATE,
  );
  const endTime = new Date().toISOString();
  return summarize({
    ...newState,
    endTime,
  },
  data);
};

const handleSessionComplete = (state: FlipState,
  data: *) => {
  const newState = recordEvent(
    state.currentIndex,
    state,
    flipEventType.COMPLETE,
  );
  const endTime = new Date().toISOString();
  return summarize({
    ...newState,
    endTime,
  }, data);
};

const flipReducer = (
  state: FlipState = { ...initialState },
  action: *,
) => {
  switch (action.type) {
    case START_SESSION:
      return handleStartSession(
        state,
        action.data,
      );
    case START_NEXT_BATCH:
      return handleFlipNextBatch(
        state,
        action.data.cards,
        action.data.inReverseMode,
        action.data.hasMore,
      );
    case SHOW:
      return handleShow(state);
    case FLIP:
      return handleFlip(state);
    case UNFLIP:
      return handleUnFlip(state);
    case SKIP:
      return handleSkip(state);
    case BACK:
      return handleBack(state);
    case NEXT:
      return handleNext(state);
    case ANSWER:
      return handleAnswer(state, action.data.confidence,
        action.data.shouldUpdateIndex);
    case STOP_SESSION:
      return handleEndSession(state,
        action.data);
    case COMPLETE_SESSION:
      return handleSessionComplete(state,
        action.data);
    case RESET_APP:
      return handleReset(state);
    default:
      return state;
  }
};

export default flipReducer;
